import { AnalyticIdentifier } from '@common/neo.tax';
import { BaseTab, BaseTabs, Stack, useTabState, BaseTabPanel } from '@ui-components/neo.tax';

import { OrganizationScope } from '@neo.tax/shared/graphql';
import { useCompanies, useUserFromAuth, useCurrentUser } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';

import { CompanyList } from '../company-list';
import { OrganizationMembers } from '../organization-members';
import {
  OrgDashboardContainer,
  ListContainer,
  StyledCompanyIcon,
  StyledMembersIcon,
  StyledTabLabel,
  StyledTabPanelContainer,
} from './organization-dashboard.styled';

export const OrganizationDashboard = (): React.ReactElement | null => {
  const companyTabId = useAnalyticId(AnalyticIdentifier.RTC_COMPANY_LIST_TAB);
  const companyMemberTabId = useAnalyticId(AnalyticIdentifier.RTC_COMPANY_MEMBER_LIST_TAB);
  const { companies } = useCompanies();
  const { isSuperUserMode } = useUserFromAuth();
  const { user } = useCurrentUser();

  const tabState = useTabState({ defaultSelectedId: companyTabId });
  const organization = user?.organization;

  const isOrgAdmin = user?.organizationScope === OrganizationScope.OrganizationAdmin;

  if (!organization) {
    return null;
  }

  const companyInvitations = companies?.flatMap((company) => company.invitedMembers);

  const isAdmin = isOrgAdmin || isSuperUserMode;

  if (!isAdmin) {
    return (
      <OrgDashboardContainer direction="column">
        <ListContainer direction="column" paddingY={8}>
          <CompanyList allowCreateCompany={false} />
        </ListContainer>
      </OrgDashboardContainer>
    );
  }

  return (
    <OrgDashboardContainer direction="column">
      <BaseTabs state={tabState} fullWidth>
        <BaseTab
          label={
            <Stack gap={1} alignItems="center" justifyContent="center">
              <StyledCompanyIcon />
              <StyledTabLabel fontSize="sm" fontWeight="medium" as="span">
                Companies
              </StyledTabLabel>
            </Stack>
          }
          tabId={companyTabId}
        />
        <BaseTab
          label={
            <Stack gap={1} alignItems="center" justifyContent="center">
              <StyledMembersIcon />
              <StyledTabLabel fontSize="sm" fontWeight="medium" as="p">
                Members
              </StyledTabLabel>
            </Stack>
          }
          tabId={companyMemberTabId}
        />
      </BaseTabs>
      <StyledTabPanelContainer>
        <BaseTabPanel state={tabState} tabId={companyTabId}>
          <ListContainer direction="column" paddingY={4}>
            <CompanyList allowCreateCompany={isOrgAdmin} />
          </ListContainer>
        </BaseTabPanel>
        <BaseTabPanel state={tabState} tabId={companyMemberTabId}>
          <ListContainer direction="column" paddingY={4}>
            <OrganizationMembers
              organizationId={organization.id}
              organizationName={organization.name}
              companies={companies || []}
              members={organization.members || []}
              companyInvitations={companyInvitations || []}
              organizationInvitations={organization.invitedMembers || []}
            />
          </ListContainer>
        </BaseTabPanel>
      </StyledTabPanelContainer>
    </OrgDashboardContainer>
  );
};
